import { Controller } from "@hotwired/stimulus";

// The counter « montant d'abord » (owner's decisions of 08/10, after the
// cashiers' demo): one amount typed, spread over the fees on screen while
// it is typed — GO4SCHOOL's fee first and whole, then instalment after
// instalment by due date; a fee that takes no part payment, or whose
// minimum is not reached, stops the spread before it. The same rules as
// CashCollections::Allocation, which records the spread; this is only what
// the cashier sees before « Encaisser ».
//
// A formula picked in « Formules facultatives » replaces the fees shown: its
// amount is filled and it alone is collected.
//
// The counter in steps (recette 09/10: the cashiers want to think as little
// as possible): a sentence says what the amount settles while it is typed,
// a hint says what is left to do, « Encaisser » carries the amount, and
// Enter moves from the amount to the method, then to the method's fields.
export default class extends Controller {
  static targets = ["amount", "take", "rest", "leftAfter", "message", "submit", "option", "optionNote", "optionLabel",
    "methodTile", "method", "summary", "hint", "collectButton"];
  static values = {
    lines: Array,
    currency: { type: String, default: "F CFA" },
    messages: Object,
    words: Object,
  };

  connect() {
    this.refresh();
  }

  // Digits only: the franc has no cents.
  typed() {
    const digits = this.amountTarget.value.replace(/\D/g, "");
    this.amountTarget.value = digits === "" ? "" : new Intl.NumberFormat("fr-FR").format(Number(digits));
    this.refresh();
  }

  // Enter on the amount goes to the method; on the method, to its first
  // field (a cheque's number…).
  nextStep(event) {
    event.preventDefault();
    if (event.currentTarget === this.amountTarget && this.hasMethodTarget) {
      // Dressed by SlimSelect: its list opens, the arrows choose.
      const slim = this.methodTarget.slim;
      if (!slim) { this.methodTarget.focus(); return; }
      slim.render.main.main.scrollIntoView({ block: "center" });
      slim.render.main.main.focus();
      slim.open();
      return;
    }
    this.focusDetails();
  }

  // A method chosen: on to its first field, once the fields are shown.
  methodChosen() {
    requestAnimationFrame(() => this.focusDetails());
  }

  focusDetails() {
    const next = [...this.element.querySelectorAll("[data-method-details] input:not([type=file]):not([type=hidden]), [data-method-details] select")]
      .find((field) => !field.disabled && field.offsetParent !== null);
    next?.focus();
  }

  // A big tile per payment method; the select behind it carries the choice
  // to the form and to the fields of the method.
  pickMethod(event) {
    event.preventDefault();
    const method = event.currentTarget.dataset.method;
    this.methodTarget.value = method;
    this.methodTarget.dispatchEvent(new Event("change", { bubbles: true }));
    this.refreshTiles();
  }

  pickOption(event) {
    event.preventDefault();
    const button = event.currentTarget;
    this.optionTarget.value = button.dataset.dueId;
    this.optionLabelTarget.textContent = button.dataset.label;
    this.optionNoteTarget.hidden = false;
    this.amountTarget.value = new Intl.NumberFormat("fr-FR").format(Number(button.dataset.amount));
    this.refresh();
    this.amountTarget.scrollIntoView({ block: "center" });
  }

  cancelOption(event) {
    event.preventDefault();
    this.optionTarget.value = "";
    this.optionNoteTarget.hidden = true;
    this.amountTarget.value = "";
    this.refresh();
  }

  amount() {
    return Number(this.amountTarget.value.replace(/\D/g, "") || 0);
  }

  maximum() {
    return this.linesValue.reduce((sum, line) => sum + line.collectable, 0);
  }

  // CashCollections::Allocation, line for line.
  spread(amount) {
    const parts = {};
    let left = amount;
    let stop = null;
    for (const line of this.linesValue) {
      if (left <= 0) break;
      const take = Math.min(left, line.collectable);
      let reason = null;
      if (take < line.collectable) {
        if (line.go4school) reason = "go4school_whole";
        else if (!line.partial_allowed) reason = "partial_not_allowed";
        else if (take < line.minimum) reason = "under_minimum";
      }
      if (reason) { stop = { line, reason }; break; }
      parts[line.id] = take;
      left -= take;
    }
    return { parts, left, stop };
  }

  refresh() {
    this.refreshTiles();
    // The « facture » page carries the method buttons alone, no amount.
    if (!this.hasAmountTarget) return;
    const option = this.hasOptionTarget && this.optionTarget.value;
    const amount = this.amount();
    const maximum = option ? amount : this.maximum();
    const { parts, left, stop } = option ? { parts: {}, left: 0, stop: null } : this.spread(amount);

    this.takeTargets.forEach((cell) => {
      const take = parts[cell.dataset.lineId] || 0;
      cell.textContent = take ? this.money(take) : "—";
      cell.closest("[data-line-row]")?.toggleAttribute("data-taken", take > 0);
    });
    this.restTargets.forEach((cell) => {
      const line = this.linesValue.find((one) => String(one.id) === cell.dataset.lineId);
      if (line) cell.textContent = this.money(line.collectable - (parts[line.id] || 0));
    });

    const taken = Object.values(parts).reduce((sum, part) => sum + part, 0);
    let message = "";
    if (amount > maximum) message = this.say("over_maximum", { maximum: this.money(maximum) });
    else if (left > 0 && stop) {
      // Nothing taken before it: the rule alone, never « encaissez 0 F ».
      const alone = taken === 0 && stop.reason !== "go4school_whole";
      message = this.say(alone ? `${stop.reason}_alone` : stop.reason, {
        label: stop.line.label, collectable: this.money(stop.line.collectable),
        minimum: this.money(stop.line.minimum), taken: this.money(taken),
      });
    }
    const ok = amount > 0 && !message;
    if (this.hasLeftAfterTarget) {
      this.leftAfterTarget.textContent = this.money(Math.max((option ? 0 : maximum) - (option ? 0 : taken), 0));
    }
    if (this.hasMessageTarget) {
      this.messageTarget.textContent = message;
      this.messageTarget.hidden = !message;
    }
    this.submitTargets.forEach((button) => { button.disabled = !ok; });
    this.refreshWords({ amount, maximum, parts, taken, option, message, ok });
  }

  // The sentence under the amount, the hint beside the button, the button.
  refreshWords({ amount, maximum, parts, taken, option, message, ok }) {
    if (this.hasSummaryTarget) {
      // The green band only with its sentence: a refused amount says why in the hint.
      const summary = this.summary({ amount, maximum, parts, taken, option });
      this.summaryTarget.innerHTML = summary;
      this.summaryTarget.hidden = !summary;
    }
    if (this.hasCollectButtonTarget) {
      this.collectButtonTarget.value = amount > 0
        ? this.word("collect_amount", { amount: this.money(amount) })
        : this.word("collect");
      // The confirmation shows the same sum in large figures (ds_confirm.js).
      this.collectButtonTarget.dataset.confirmAmount = amount > 0 ? this.money(amount) : "";
    }
    if (this.hasHintTarget) {
      const missing = this.missingDetail();
      // No amount yet: the green sentence already asks for it (recette 09/10).
      let hint = this.word("hint_ready");
      if (amount === 0) hint = "";
      else if (!ok) hint = message;
      else if (missing) hint = this.word("hint_details", { field: missing });
      this.hintTarget.textContent = hint;
      this.hintTarget.hidden = !hint;
      this.hintTarget.classList.toggle("is-refused", amount > 0 && !ok);
    }
  }

  // The first required field of the method still empty, by its label.
  missingDetail() {
    const field = [...this.element.querySelectorAll("[data-method-details] [required]")]
      .find((one) => !one.disabled && one.type !== "file" && !one.value.trim());
    return field?.closest(".ds-field")?.querySelector(".ds-field__label")?.textContent?.replace("*", "").trim();
  }

  summary({ amount, maximum, parts, taken, option }) {
    if (amount === 0) return this.word("summary_empty", { left: this.money(maximum) });
    if (option) return this.word("summary_formula", { label: this.escape(this.optionLabelTarget.textContent) });

    const list = this.linesValue.filter((line) => parts[line.id]).map((line) => (
      parts[line.id] === line.collectable
        ? this.word("summary_whole", { label: this.escape(line.label) })
        : this.word("summary_part", { label: this.escape(line.label), amount: this.money(parts[line.id]) })
    ));
    if (list.length === 0) return "";
    const left = maximum - taken;
    return `${this.word("summary_takes", { list: list.join(", ") })} ${
      left > 0 ? this.word("summary_left", { left: this.money(left) }) : this.word("summary_settled")}`;
  }

  word(key, values = {}) {
    let text = this.wordsValue[key] || "";
    Object.entries(values).forEach(([name, value]) => { text = text.replaceAll(`%{${name}}`, value); });
    return text;
  }

  escape(text) {
    const span = document.createElement("span");
    span.textContent = text;
    return span.innerHTML;
  }

  refreshTiles() {
    if (!this.hasMethodTarget) return;
    this.methodTileTargets.forEach((tile) => {
      const chosen = tile.dataset.method === this.methodTarget.value;
      tile.setAttribute("aria-pressed", chosen);
      tile.classList.toggle("is-chosen", chosen);
    });
  }

  say(key, values) {
    let text = this.messagesValue[key] || "";
    Object.entries(values).forEach(([name, value]) => { text = text.replaceAll(`%{${name}}`, value); });
    return text;
  }

  money(amount) {
    return `${new Intl.NumberFormat("fr-FR").format(amount)} ${this.currencyValue}`;
  }
}
